import type { TinyAIPageContext } from '@repo/types';
import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getHomePageTinyAccountId } from 'store/home';
import {
  getTinyAILatestKitForMainAccount,
  getTinyAILatestKitForTinyAccountId,
} from 'store/kits';
import { useCurrentKitAndTinyAccount } from './useCurrentKitAndTinyAccount';
import { useTinyAIPageSource } from './useTinyAIPageSource';

export const useTinyAIPageContext = (): TinyAIPageContext => {
  const location = useLocation();
  const params = useParams<{
    subMetricId?: string;
    actionId?: string;
  }>();

  const { currentKit, currentKitId, currentTinyAccountId } =
    useCurrentKitAndTinyAccount();
  const pageSource = useTinyAIPageSource();

  const homePageTinyAccountId = useSelector(getHomePageTinyAccountId);
  const isHomeRoute = location.pathname === '/';
  const isHomeScopedToTinyAccount = isHomeRoute && !!homePageTinyAccountId;
  const homeFallbackKit = useSelector(
    getTinyAILatestKitForTinyAccountId(
      isHomeScopedToTinyAccount ? homePageTinyAccountId : null,
    ),
  );
  const mainAccountFallbackKit = useSelector(getTinyAILatestKitForMainAccount());

  const fallbackKit = isHomeScopedToTinyAccount
    ? homeFallbackKit
    : mainAccountFallbackKit;
  const resolvedKit = currentKit ?? fallbackKit ?? null;
  const resolvedKitId = currentKitId ?? resolvedKit?.id;
  const resolvedTinyAccountId =
    currentTinyAccountId ?? resolvedKit?.tinyaccount_id ?? null;

  return useMemo(() => {
    const pathname = location.pathname;

    const baseContext: TinyAIPageContext = {
      url: pathname,
      source: pageSource,
    };

    if (resolvedKitId) {
      baseContext.kitId = resolvedKitId;
    }

    if (resolvedTinyAccountId) {
      baseContext.tinyAccountId = resolvedTinyAccountId;
    }

    if (resolvedKit?.pregnant != null) {
      baseContext.isPregnant = Boolean(resolvedKit.pregnant);
    }

    if (params.subMetricId && /^\d+$/.test(params.subMetricId)) {
      baseContext.subMetricId = params.subMetricId;
    } else {
      const insightsMatch = pathname.match(/\/insights\/[^/]+\/(\d+)/);
      if (insightsMatch) {
        baseContext.subMetricId = insightsMatch[1];
      }
    }

    const actionPlanMatch = pathname.match(/\/action\/(\d+)/);
    if (actionPlanMatch) {
      baseContext.actionPlanItemId = actionPlanMatch[1];
    }

    return baseContext;
  }, [
    location.pathname,
    params.subMetricId,
    params.actionId,
    resolvedKit?.pregnant,
    resolvedKitId,
    resolvedTinyAccountId,
    pageSource,
  ]);
};
